// Check if current environment is Nexos
const isNexos = () => process.env.VUE_APP_CUSTOMER === 'nexos';

// Format path with Nexos prefix if needed
const formatPath = (path) => {
  if (!path) return path;
  return isNexos() && !path.startsWith('/nexos') 
    ? `/nexos${path.startsWith('/') ? '' : '/'}${path}`
    : path;
};

/**
 * Converts a slug to a human-readable nombreFlujo format
 * @param {string} slug - The slug to convert (e.g., 'laboratorio-de-ideas')
 * @returns {string} - Human-readable format (e.g., 'laboratorio de ideas')
 */
const slugToNombreFlujo = (slug) => {
  if (!slug) return 'home';
  
  // Single-word slugs that should remain as-is
  const singleWordSlugs = ['nexos', 'home', 'login', 'register', 'signup', 'perfil', 'conocenos'];
  if (singleWordSlugs.includes(slug)) {
    return slug === 'nexos' ? 'home' : slug;
  }
  
  // Convert hyphens to spaces for multi-word slugs
  return slug.replace(/-/g, ' ');
};

/**
 * Gets the nombreFlujo from the current route
 * @param {Object} route - Vue router route object (optional, will try to get from context if not provided)
 * @returns {string} - The flow name in human-readable format (e.g., 'laboratorio de ideas' instead of 'laboratorio-de-ideas')
 */
export const getNombreFlujo = (route = null) => {
  let slugFromRoute = null;
  
  // If route is provided, use it
  if (route && route.params) {
    slugFromRoute = route.params.communityId;
  } else if (typeof window !== 'undefined') {
    // Fallback: try to get from window context if available
    if (window.$nuxt && window.$nuxt.$route && window.$nuxt.$route.params) {
      slugFromRoute = window.$nuxt.$route.params.communityId;
    } else if (window.$router && window.$router.currentRoute && window.$router.currentRoute.params) {
      slugFromRoute = window.$router.currentRoute.params.communityId;
    }
  }
  
  return slugToNombreFlujo(slugFromRoute);
};

/**
 * Track events in dataLayer for analytics
 * @param {Object} options - The event options
 * @param {string} options.eventType - The type of event (e.g., 'PAGEVIEW', 'MENU', 'BUTTON', 'LINK')
 * @param {string} options.nombreFlujo - The flow name (e.g., 'login', 'home')
 * @param {string} [options.seccion='nexos'] - The section name
 * @param {string} [options.activo='bancolombia'] - The active entity
 * @param {string} [options.path] - The page path (for pageview events)
 * @param {string} [options.menu] - The menu name (for menu events)
 * @param {string} [options.elemento] - The element that was interacted with
 * @param {string} [options.ubicacion] - The location of the element (e.g., 'header', 'footer')
 * @param {string} [options.urlDestino] - The destination URL (for link events)
 */
export function trackDataLayerEvent({
  eventType,
  nombreFlujo,
  seccion = 'nexos',
  activo = 'bancolombia',
  path,
  menu,
  elemento,
  ubicacion,
  modal,
  urlDestino,
}) {
  if (typeof window === 'undefined' || !window.dataLayer || !isNexos()) return;

  const eventMap = {
    PAGEVIEW: 'SEND_PAGEVIEW_BANCOLOMBIA',
    MENU: 'SEND_MENU_BANCOLOMBIA',
    BUTTON: 'SEND_BOTONES_BANCOLOMBIA',
    LINK: 'SEND_LINKS_BANCOLOMBIA',
    CARD: 'SEND_CARDS_BANCOLOMBIA',
    MODALVIEW: 'SEND_PAGEVIEW_MODAL_BANCOLOMBIA',
  };

  const baseEvent = {
    event: eventMap[eventType] || eventType,
    activo,
    nombreFlujo,
    seccion,
  };

  // Add optional properties if they exist
  if (path) baseEvent.path = formatPath(path);
  if (menu) baseEvent.menu = menu;
  if (elemento) baseEvent.elemento = elemento;
  if (ubicacion) baseEvent.ubicacion = ubicacion;
  if (modal) baseEvent.modal = modal;
  if (urlDestino) baseEvent.urlDestino = urlDestino;

  console.log('trackDataLayerEvent', baseEvent);

  window.dataLayer.push(baseEvent);
}

// Helper functions for common event types
export const trackPageview = (nombreFlujo, path) => {
  trackDataLayerEvent({
    eventType: 'PAGEVIEW',
    nombreFlujo,
    path: formatPath(path),
  });
};
export const trackModalPageview = (nombreFlujo, modal, path) => {
  trackDataLayerEvent({
    eventType: 'MODALVIEW',
    nombreFlujo,
    modal,
    path: formatPath(path),
  });
};

export const trackMenuClick = (nombreFlujo, menu, elemento, ubicacion) => {
  trackDataLayerEvent({
    eventType: 'MENU',
    nombreFlujo,
    menu,
    elemento,
    ubicacion,
  });
};

export const trackButtonClick = (nombreFlujo, elemento, ubicacion, modal) => {
  trackDataLayerEvent({
    eventType: 'BUTTON',
    nombreFlujo,
    elemento,
    ubicacion,
    modal,
  });
};

export const trackLinkClick = (nombreFlujo, elemento, ubicacion, urlDestino) => {
  trackDataLayerEvent({
    eventType: 'LINK',
    nombreFlujo,
    elemento,
    ubicacion,
    urlDestino,
  });
};

export const trackCardClick = (nombreFlujo, elemento, ubicacion) => {
  trackDataLayerEvent({
    eventType: 'CARD',
    nombreFlujo,
    elemento,
    ubicacion,
  });
};